import React, { useState, useEffect, useRef } from 'react';
import Modal from 'react-bootstrap/Modal';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import '../styles/SectionTracking.scss';
import { useTranslation } from 'react-i18next';
import { getToken } from './sessionManager';
import useUserSession from './useUserSession';

// Fix default marker icon issue with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

function FitBounds({ positions }) {
  const map = useMap();
  useEffect(() => {
    if (positions.length > 0) {
      const bounds = L.latLngBounds(positions.map(p => [p.latitude, p.longitude]));
      map.fitBounds(bounds, { padding: [30, 30] });
    }
  }, [positions, map]);
  return null;
}

function SectionTracking({ show, onHide }) {
  const { t } = useTranslation();
  const { user } = useUserSession();
  const [blNumber, setBlNumber] = useState('');
  const [events, setEvents] = useState([]);
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const inputRef = useRef(null);

  const token = user?.token || getToken();

  useEffect(() => {
    if (show && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
    if (!show) {
      setEvents([]);
      setStatus('idle');
      setErrorMsg('');
      setBlNumber('');
    }
  }, [show]);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!blNumber.trim()) return;

    if (!token) {
      setStatus('error');
      setErrorMsg(t('tracking.loginRequired'));
      return;
    }

    setStatus('loading');
    setErrorMsg('');
    setEvents([]);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/tracking/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ carrierBookingReference: blNumber.trim() }),
      });

      if (!response.ok) {
        setStatus('error');
        setErrorMsg(t('tracking.error'));
        return;
      }

      const data = await response.json();

      if (data.status === 0 || data.status === 2) {
        setEvents(data.events || []);
        if ((data.events || []).length === 0) {
          setStatus('empty');
        } else {
          setStatus('success');
        }
      } else {
        setStatus('error');
        setErrorMsg(data.message || t('tracking.error'));
      }
    } catch {
      setStatus('error');
      setErrorMsg(t('tracking.error'));
    }
  };

  const geoEvents = events.filter(e => e.latitude != null && e.longitude != null);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      className="SectionTracking"
    >
      <Modal.Header closeButton>
        <Modal.Title>{t('tracking.title')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form className="SectionTracking_search" onSubmit={handleSearch}>
          <input
            ref={inputRef}
            type="text"
            className="form-control"
            placeholder={t('tracking.placeholder')}
            value={blNumber}
            onChange={(e) => setBlNumber(e.target.value)}
          />
          <button type="submit" className="btn" disabled={status === 'loading' || !blNumber.trim()}>
            {status === 'loading' ? (
              <span className="spinner-border spinner-border-sm" role="status"></span>
            ) : (
              t('tracking.search')
            )}
          </button>
        </form>

        {status === 'error' && (
          <div className="SectionTracking_error">{errorMsg}</div>
        )}

        {status === 'empty' && (
          <div className="SectionTracking_empty">{t('tracking.noResults')}</div>
        )}

        {!token && status === 'idle' && (
          <div className="SectionTracking_info">{t('tracking.loginRequired')}</div>
        )}

        <div className="SectionTracking_map">
          <MapContainer
            center={[20, 0]}
            zoom={2}
            style={{ width: '100%', height: '100%' }}
            scrollWheelZoom={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {geoEvents.map((event, index) => (
              <Marker key={index} position={[event.latitude, event.longitude]}>
                <Popup>
                  <strong>{event.eventDescription || event.eventType}</strong><br />
                  {event.locationName && <>{event.locationName}<br /></>}
                  {event.vesselName && <><em>{event.vesselName}</em><br /></>}
                  {event.eventDateTime && <span>{formatDate(event.eventDateTime)}</span>}
                </Popup>
              </Marker>
            ))}
            {geoEvents.length > 0 && <FitBounds positions={geoEvents} />}
          </MapContainer>
        </div>

        {events.length > 0 && (
          <div className="SectionTracking_events">
            <table>
              <thead>
                <tr>
                  <th>{t('tracking.event')}</th>
                  <th>{t('tracking.location')}</th>
                  <th>{t('tracking.date')}</th>
                  <th>{t('tracking.transport')}</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event, index) => (
                  <tr key={index}>
                    <td>{event.eventDescription || event.eventType || '-'}</td>
                    <td>{event.locationName || event.unLocationCode || '-'}</td>
                    <td>{formatDate(event.eventDateTime)}</td>
                    <td>{event.transportMode || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default SectionTracking;
